import {
  Root,
  Trigger,
  Portal,
  Content,
} from "@radix-ui/react-popover";
import { Check, ChevronDown } from "lucide-react";
import { useId, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface MultiSelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
}

export function MultiSelect<V extends string = string>({
  options,
  value,
  onChange,
  placeholder = "Select…",
  className,
  triggerLabel,
}: {
  options: MultiSelectOption<V>[];
  value: V[];
  onChange: (next: V[]) => void;
  placeholder?: string;
  className?: string;
  triggerLabel?: ReactNode;
}) {
  const id = useId();

  function toggle(v: V) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  const selectedSummary =
    triggerLabel ??
    (value.length === 0
      ? placeholder
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? value[0]
        : `${value.length} selected`);

  return (
    <Root>
      <Trigger
        className={cn(
          "inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-neutral-800",
          "bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-100",
          "transition-colors hover:border-neutral-700 focus-visible:border-accent-500",
          "data-[state=open]:border-accent-500",
          className,
        )}
      >
        <span
          className={cn(
            "truncate",
            value.length === 0 && !triggerLabel && "text-neutral-500",
          )}
        >
          {selectedSummary}
        </span>
        <ChevronDown
          aria-hidden
          size={14}
          className="shrink-0 text-neutral-500 transition-transform data-[state=open]:rotate-180"
        />
      </Trigger>
      <Portal>
        <Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-50 max-h-72 w-(--radix-popover-trigger-width) min-w-44 overflow-y-auto",
            "rounded-md border border-neutral-800 bg-neutral-900 p-1 shadow-md",
          )}
        >
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            const optId = `${id}-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={optId}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs",
                  "text-neutral-200 hover:bg-neutral-800",
                )}
              >
                <input
                  id={optId}
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                />
                <span
                  aria-hidden
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                    checked
                      ? "border-accent-500 bg-accent-600 text-neutral-50"
                      : "border-neutral-700 bg-neutral-950",
                  )}
                >
                  {checked ? <Check size={10} strokeWidth={3} /> : null}
                </span>
                <span className="truncate">{opt.label}</span>
              </label>
            );
          })}
        </Content>
      </Portal>
    </Root>
  );
}
